<template lang="pug">
v-container.fill-height(fluid)
    v-snackbar(v-model="showErrorSnackbar" color="error")
        span {{ errorMessage }}

    v-row.align-center.justify-center(v-if="!isKioskMode")
        v-col(xs="12" sm="8" md="6" lg="3")
            v-card.text-xs-center
                //v-card-title {{ $t('toolbarPage.appLoginTitle') }}
                v-row.ma-0.mt-0(justify="end")
                  v-col.text-start.title(cols="8" md="7" align="start") {{ $t('toolbarPage.appLoginTitle') }}
                  v-col.text-end.title(cols="4" md="5" align="end")
                    LanguageSelectionButton
                v-layout.logo(align-center justify-center)
                    div
                        v-responsive
                            v-img.mobile-logo(alt="LM" :src="require('@/assets/1.0-single-image-banner-block-1700x385px.jpg')")
                v-card-title {{ $t('loginPage.loginLabel') }}
                v-row.ma-0(v-if="isInvalidToken")
                  v-col.pb-0(cols="12")
                    v-alert(type="error" border="top") {{ $t('errors.invalidTokenMessage') }}
                v-card-text
                    v-form(v-model="formIsValid" ref="loginForm" lazy-validation @submit.prevent="onFormSubmit" :disabled="fetchInProgress")
                        v-text-field(v-model="username" :label="`* ${$t('loginPage.userNameFieldLabel')}`" :rules="usernameValidationRules")
                        v-text-field(
                            autocomplete
                            v-model="password"
                            :label="`* ${$t('loginPage.passwordFieldLabel')}`"
                            :append-icon="passwordFieldIcon"
                            :rules="passwordValidationRules"
                            :type="passwordFieldType"
                            @click:append="onToggleShowPasswordClick"
                        )
                        v-btn.login-button(block color="primary" type="submit" :disabled="!formIsValid" :loading="fetchInProgress") {{ $t('loginPage.loginButtonLabel') }}
                        v-row.justify-center.mt-4
                            v-btn.text-decoration-underline.text-none(color="primary" text small   @click="onPasswordRecoveryButtonClick") {{ $t('loginPage.passwordRecoveryButtonLabel') }}
    template(v-else )
      LoginPageKiosk

</template>

<script lang="ts">
import Vue from 'vue'
import {GET_MODE, IS_INVALID_TOKEN, IS_USER_LOGGED_IN} from '@/store/getter-types'
import {AUTH_LOG_USER_IN, SET_INVALID_TOKEN} from '@/store/action-types'
import {UserCredentialsPayload} from '@/types/payload'
import InvalidCredentialsError from '@/model/error/InvalidCredentialsError'
import {getSetting} from '@/services/settings'
import {Component, Watch} from 'vue-property-decorator'
import {TranslateResult} from "vue-i18n";
import {Action, Getter} from "vuex-class";
import LoginPageKiosk from "@/pages/LoginPageKiosk.vue";
import moment from "moment/moment";
import i18n from "@/plugins/i18n";
import LanguageSelectionButton from "@/components/user/LanguageSelectionButton.vue";

@Component({
  components: {
    LoginPageKiosk,
    LanguageSelectionButton
  }
})
export default class LoginPage extends Vue {
  $refs!: {
    loginForm: Vue & { validate(): boolean }
  }

  private username: string | null = null
  private password: string | null = null
  private showPassword: boolean = false

  private usernameValidationRules: Function[] = [
    (v: string): boolean | string => !!v || i18n.t('validation.valueIsRequired').toString()
  ]

  private passwordValidationRules: Function[] = [
    (v: string): boolean | string => !!v || i18n.t('validation.valueIsRequired').toString()
  ]

  private formIsValid: boolean = false
  private showErrorSnackbar: boolean = false
  private errorMessage: TranslateResult | null = null
  private fetchInProgress: boolean = false

  @Action(AUTH_LOG_USER_IN) performLogin!: { (payload: UserCredentialsPayload): Promise<void> }
  @Getter(IS_INVALID_TOKEN) isInvalidToken!: boolean
  @Action(SET_INVALID_TOKEN) setInvalidToken!: { (payload: boolean): void }

  get isKioskMode (): boolean {
    return this.$store.getters[GET_MODE] === 'kiosk'
  }

  get isUserLoggedIn(): boolean {
    return this.$store.getters[IS_USER_LOGGED_IN]
  }

  get passwordFieldType(): string {
    return this.showPassword ? 'text' : 'password'
  }

  get passwordFieldIcon(): string {
    return this.showPassword ? 'fa-eye-slash' : 'fa-eye'
  }

  @Watch('isUserLoggedIn')
  onIsUserLoggedInChange(newVal: boolean): void {
    const changePin = getSetting('boarder.force_pin_change')
    if (changePin && changePin.value === 'true' && !this.isKioskMode) {
      this.$router.push({name: 'ChangePin'})
    } else {
      if (newVal && !this.isKioskMode) {
        this.$router.push({name: 'Dashboard'})
      } else if(newVal && this.isKioskMode) {
        this.$router.push({
          name: 'Order',
          params: {
            isoWeek: moment().format('GGGG[W]WW'),
            date: moment().format('YYYY-MM-DD')
          }
        })
      }
    }
  }

  onToggleShowPasswordClick(): void {
    this.showPassword = !this.showPassword
  }

  onFormSubmit(): void {
    if (this.$refs.loginForm.validate()) {
      const payload: UserCredentialsPayload = {
        username: this.username!,
        password: this.password!
      }
      this.fetchInProgress = true
      this.setInvalidToken(false)

      this.$store.dispatch(AUTH_LOG_USER_IN, payload)
        .catch((e: InvalidCredentialsError) => {
          this.errorMessage = e.message
          this.showErrorSnackbar = true
          this.fetchInProgress = false
        })

      //this.$store.commit(SET_CURRENT_DAILYMENU_TOP_GROUP_OFFER, undefined)
    }
  }

  onPasswordRecoveryButtonClick(): void {
    this.$router.push({name: 'PasswordRecovery'})
  }

  mounted(): void {
    this.errorMessage = this.$t('loginPage.loginFail')
  }
}
</script>

<style lang="stylus" scoped>

html
  overflow hidden !important

  .login-button
    margin-top 2em
    height 3.5em


  .mobile-logo
    width 100%
    height 220px

  .logo
    margin 5px 20px
    padding 5px 20px
    overflow hidden

  .foot
    text-align center

  .version
    text-align center
    margin 10px 0
    padding 5px

@media (max-height: 600px)
  .mobile-logo
    width 60px
    height 60px

  .welcome-text
    font-size 0.8em

@media (max-height: 400px)
  .welcome-text
    display none
</style>
